import { isCliImmediateShutdownRequested } from '../../../runtime/cli-process-interrupt.js';
import { CliSqliteInterrupted } from '../../../sqlite/sqlite-errors.js';
import { buildSymbolIndexInWorkerHost } from '../build/build-symbol-index-host.js';
import { ensureSymbolIndexFileInWorkerHost } from '../build/ensure-symbol-index-file-host.js';
import {
  removeSymbolIndexForFileInWorkerHost,
  updateSymbolIndexForFileInWorkerHost,
} from '../build/update-symbol-index-file-host.js';
import { postSafe } from './post-message.js';
import type { SymbolIndexWorkerInMessage, SymbolIndexWorkerPort } from './protocol.js';

import { isSymbolIndexWorkerShutdownRequested } from './shutdown-state.js';

function postWorkerError(port: SymbolIndexWorkerPort, requestId: string, err: unknown): void {
  if (
    isCliImmediateShutdownRequested() ||
    isSymbolIndexWorkerShutdownRequested() ||
    err instanceof CliSqliteInterrupted
  ) {
    postSafe(port, {
      type: 'error',
      requestId,
      message: new CliSqliteInterrupted().message,
    });
    return;
  }
  postSafe(port, {
    type: 'error',
    requestId,
    message: err instanceof Error ? err.message : String(err),
  });
}

export async function handleIndexWorkerMessage(
  port: SymbolIndexWorkerPort,
  msg: SymbolIndexWorkerInMessage,
): Promise<void> {
  switch (msg.type) {
    case 'buildFull': {
      try {
        const result = await buildSymbolIndexInWorkerHost(msg.parentPath);
        postSafe(port, { type: 'buildComplete', requestId: msg.requestId, ...result });
      } catch (err) {
        postWorkerError(port, msg.requestId, err);
      }
      return;
    }
    case 'updateFile': {
      try {
        await updateSymbolIndexForFileInWorkerHost(msg.parentPath, msg.relPath);
        postSafe(port, { type: 'updateComplete', requestId: msg.requestId });
      } catch (err) {
        postWorkerError(port, msg.requestId, err);
      }
      return;
    }
    case 'removeFile': {
      try {
        await removeSymbolIndexForFileInWorkerHost(msg.parentPath, msg.relPath);
        postSafe(port, { type: 'removeComplete', requestId: msg.requestId });
      } catch (err) {
        postWorkerError(port, msg.requestId, err);
      }
      return;
    }
    case 'ensureFile': {
      try {
        const ready = await ensureSymbolIndexFileInWorkerHost(msg.parentPath, msg.filePath, {
          importDepth: msg.importDepth,
        });
        postSafe(port, { type: 'ensureComplete', requestId: msg.requestId, ready });
      } catch (err) {
        postWorkerError(port, msg.requestId, err);
      }
      return;
    }
    default:
      return;
  }
}
